"use client";

import {
  SAVED_RECEIPTS_LOCAL_STORAGE_KEY,
  savedReceiptsCollectionSchema,
  type SavedReceipt,
  type SavedReceiptsCollection,
  type InvoiceData,
} from "@/app/schema";

// Re-export types for consumers
export type { SavedReceipt, SavedReceiptsCollection };
import dayjs from "dayjs";
import {
  encryptReceiptData,
  decryptReceiptData,
  isReceiptDataEncrypted,
} from "@/lib/receipt-encryption";

/**
 * Default empty receipts collection
 */
const DEFAULT_RECEIPTS_COLLECTION: SavedReceiptsCollection = {
  receipts: [],
  version: "1.0",
};

/**
 * In-memory cache for localStorage reads
 * Follows the "js-cache-storage" pattern from Vercel best practices
 */
const receiptsCache: {
  data: SavedReceiptsCollection | null;
  timestamp: number;
} = {
  data: null,
  timestamp: 0,
};

/** Cache TTL: 5 seconds to balance performance and freshness */
const CACHE_TTL = 5000;

/**
 * Invalidate the cache - call after any write operation
 */
function invalidateCache(): void {
  receiptsCache.data = null;
  receiptsCache.timestamp = 0;
}

/**
 * Listen for storage changes from other tabs/windows
 * and invalidate cache when data changes externally
 */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === SAVED_RECEIPTS_LOCAL_STORAGE_KEY) {
      invalidateCache();
    }
  });
}

/**
 * Generate a unique ID for a new receipt
 * Uses cryptographically secure random values for high entropy
 */
function generateReceiptId(): string {
  try {
    // Use crypto.getRandomValues for cryptographically secure random bytes
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);

    // Convert to hex string
    const randomHex = Array.from(array, (b) =>
      b.toString(16).padStart(2, "0"),
    ).join("");

    // Add timestamp for chronological sorting
    const timestamp = Date.now().toString(36);

    return `${timestamp}-${randomHex}`;
  } catch {
    // Fallback for environments without crypto
    const fallbackRandom =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    return `${Date.now()}-${fallbackRandom}`;
  }
}

/**
 * Get the display name for a receipt based on its data
 */
function getReceiptDisplayName(invoiceData: InvoiceData): string {
  const receiptNumber = invoiceData.receiptNumber?.number || "-";
  const buyerName = invoiceData.buyer?.name || "לקוח לא ידוע";

  return `קבלה ${receiptNumber} - ${buyerName}`;
}

/**
 * Retrieves all saved receipts from local storage
 * Uses in-memory caching to avoid repeated localStorage reads
 * Follows the "js-cache-storage" pattern from Vercel best practices
 *
 * @param password - Optional password for decrypting encrypted receipts
 * @returns Promise resolving to the receipts collection
 */
export async function getSavedReceipts(
  password?: string,
): Promise<SavedReceiptsCollection> {
  const now = Date.now();

  // Return cached data if still fresh
  if (receiptsCache.data && now - receiptsCache.timestamp < CACHE_TTL) {
    if (process.env.NODE_ENV === "development") {
      console.log("[getSavedReceipts] Cache hit");
    }
    return receiptsCache.data;
  }

  try {
    const savedData = localStorage.getItem(SAVED_RECEIPTS_LOCAL_STORAGE_KEY);
    if (process.env.NODE_ENV === "development") {
      console.log(
        "[getSavedReceipts] Raw localStorage data:",
        savedData ? "found" : "not found",
      );
    }

    if (!savedData) {
      receiptsCache.data = DEFAULT_RECEIPTS_COLLECTION;
      receiptsCache.timestamp = now;
      return DEFAULT_RECEIPTS_COLLECTION;
    }

    // Check if data is encrypted and decrypt if necessary
    let parsedData: unknown;

    if (isReceiptDataEncrypted(savedData)) {
      // Data is encrypted, attempt to decrypt
      if (!password) {
        // MANDATORY ENCRYPTION: Cannot access encrypted receipts without password
        // Return empty collection - user must provide password to decrypt
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "[getSavedReceipts] Encrypted receipts exist but no password provided. User must enter password to access receipts.",
          );
        }
        receiptsCache.data = DEFAULT_RECEIPTS_COLLECTION;
        receiptsCache.timestamp = now;
        return DEFAULT_RECEIPTS_COLLECTION;
      }

      const decrypted = await decryptReceiptData(savedData, password);
      if (!decrypted) {
        if (process.env.NODE_ENV === "development") {
          console.error("[getSavedReceipts] Failed to decrypt receipt data");
        }
        receiptsCache.data = DEFAULT_RECEIPTS_COLLECTION;
        receiptsCache.timestamp = now;
        return DEFAULT_RECEIPTS_COLLECTION;
      }

      parsedData = JSON.parse(decrypted);
    } else {
      // Legacy unencrypted data
      parsedData = JSON.parse(savedData);
    }

    if (process.env.NODE_ENV === "development") {
      console.log(
        "[getSavedReceipts] Parsed receipts count:",
        (parsedData as SavedReceiptsCollection).receipts?.length || 0,
      );
    }

    const validatedData = savedReceiptsCollectionSchema.safeParse(parsedData);

    if (!validatedData.success) {
      if (process.env.NODE_ENV === "development") {
        console.error(
          "[getSavedReceipts] Error validating saved receipts:",
          validatedData.error,
        );
      }
      receiptsCache.data = DEFAULT_RECEIPTS_COLLECTION;
      receiptsCache.timestamp = now;
      return DEFAULT_RECEIPTS_COLLECTION;
    }

    // Update cache
    receiptsCache.data = validatedData.data;
    receiptsCache.timestamp = now;

    return validatedData.data;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[getSavedReceipts] Error loading saved receipts:", error);
    }
    receiptsCache.data = DEFAULT_RECEIPTS_COLLECTION;
    receiptsCache.timestamp = now;
    return DEFAULT_RECEIPTS_COLLECTION;
  }
}

/**
 * Saves a new receipt to local storage
 * @param invoiceData - The invoice data to save
 * @param password - Optional password for encryption (required if encryption is enabled)
 * @returns Promise resolving to the ID of the newly created receipt
 * @throws Error if saving fails or encryption is required but no password provided
 */
export async function saveReceipt(
  invoiceData: InvoiceData,
  password?: string,
): Promise<string> {
  try {
    const collection = await getSavedReceipts(password);

    const id = generateReceiptId();
    const now = dayjs().toISOString();

    const newReceipt: SavedReceipt = {
      id,
      invoiceData,
      createdAt: now,
      updatedAt: now,
      displayName: getReceiptDisplayName(invoiceData),
    };

    const updatedCollection: SavedReceiptsCollection = {
      ...collection,
      receipts: [newReceipt, ...collection.receipts],
    };

    const validatedCollection =
      savedReceiptsCollectionSchema.safeParse(updatedCollection);

    if (!validatedCollection.success) {
      if (process.env.NODE_ENV === "development") {
        console.error(
          "[saveReceipt] Validation error:",
          validatedCollection.error,
        );
      }
      throw new Error("Failed to validate receipt data");
    }

    // MANDATORY ENCRYPTION: All receipts must be encrypted
    // No unencrypted fallback allowed - legally required for compliance
    if (!password) {
      throw new Error(
        "Encryption password required but not provided. Receipts must be encrypted.",
      );
    }

    const jsonData = JSON.stringify(validatedCollection.data);
    const dataToStore = await encryptReceiptData(jsonData, password);

    localStorage.setItem(SAVED_RECEIPTS_LOCAL_STORAGE_KEY, dataToStore);

    // Invalidate cache after write
    invalidateCache();

    return id;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[saveReceipt] Error saving receipt:", error);
    }
    throw error;
  }
}

/**
 * Deletes a receipt from local storage
 * @param id - The ID of the receipt to delete
 * @param password - Optional password for decrypting/encrypting during deletion
 */
export async function deleteReceipt(
  id: string,
  password?: string,
): Promise<void> {
  try {
    const collection = await getSavedReceipts(password);

    const updatedCollection: SavedReceiptsCollection = {
      ...collection,
      receipts: collection.receipts.filter((r) => r.id !== id),
    };

    // MANDATORY ENCRYPTION: All receipts must be encrypted
    // No unencrypted fallback allowed - legally required for compliance
    if (!password) {
      throw new Error(
        "Encryption password required but not provided. Receipts must be encrypted.",
      );
    }

    const jsonData = JSON.stringify(updatedCollection);
    const dataToStore = await encryptReceiptData(jsonData, password);

    localStorage.setItem(SAVED_RECEIPTS_LOCAL_STORAGE_KEY, dataToStore);

    // Invalidate cache after write
    invalidateCache();
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[deleteReceipt] Error deleting receipt:", error);
    }
    throw error;
  }
}

/**
 * Gets a single receipt by ID
 * @param id - The ID of the receipt to retrieve
 * @param password - Optional password for decrypting
 * @returns Promise resolving to the receipt or null if not found
 */
export async function getReceiptById(
  id: string,
  password?: string,
): Promise<SavedReceipt | null> {
  try {
    const collection = await getSavedReceipts(password);
    return collection.receipts.find((r) => r.id === id) || null;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[getReceiptById] Error getting receipt:", error);
    }
    return null;
  }
}

/**
 * Migrate existing receipt data to encrypted format
 *
 * @param password - Password for encrypting the migrated data
 * @returns Promise resolving to true if migration succeeded, false otherwise
 */
export async function migrateReceiptsToEncrypted(
  password: string,
): Promise<boolean> {
  try {
    const savedData = localStorage.getItem(SAVED_RECEIPTS_LOCAL_STORAGE_KEY);

    if (!savedData) {
      if (process.env.NODE_ENV === "development") {
        console.log("[migrateReceiptsToEncrypted] No receipts to migrate");
      }
      return true;
    }

    // Check if already encrypted
    if (isReceiptDataEncrypted(savedData)) {
      if (process.env.NODE_ENV === "development") {
        console.log("[migrateReceiptsToEncrypted] Receipts already encrypted");
      }
      return true;
    }

    // Validate data format
    const parsed = JSON.parse(savedData);
    const validated = savedReceiptsCollectionSchema.safeParse(parsed);

    if (!validated.success) {
      if (process.env.NODE_ENV === "development") {
        console.error(
          "[migrateReceiptsToEncrypted] Invalid receipt data format:",
          validated.error,
        );
      }
      return false;
    }

    // Encrypt the data
    const encrypted = await encryptReceiptData(savedData, password);

    // Store encrypted data
    localStorage.setItem(SAVED_RECEIPTS_LOCAL_STORAGE_KEY, encrypted);

    // Enable encryption preference
    const { setReceiptEncryptionEnabled } =
      await import("@/lib/receipt-encryption");
    setReceiptEncryptionEnabled(true);

    // Invalidate cache
    invalidateCache();

    if (process.env.NODE_ENV === "development") {
      console.log("[migrateReceiptsToEncrypted] Migration successful");
    }
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[migrateReceiptsToEncrypted] Migration failed:", error);
    }
    return false;
  }
}

/**
 * Check if stored receipts are encrypted
 * @returns true if encrypted, false if plaintext or no data
 */
export function areReceiptsEncrypted(): boolean {
  try {
    const savedData = localStorage.getItem(SAVED_RECEIPTS_LOCAL_STORAGE_KEY);
    if (!savedData) return false;
    return isReceiptDataEncrypted(savedData);
  } catch {
    return false;
  }
}
